import { z } from "zod/v4";

/** Administrative audit log (« Journal »). Spec:
 *  docs/superpowers/specs/2026-08-29-audit-log-design.md */

export const AUDIT_ACTIONS = [
  "org.create",
  "org.delete",
  "org.rename",
  "org.suspend",
  "org.activate",
  "org.update_permissions",
  "org.set_logo",
  "org.set_reglement",
  "org.set_location",
  "org.set_spot_arrival_policy",
  "org.set_timezone",
  "org.set_day_end",
  "org.set_exit_tracking",
  "org.set_walk_in_id_policy",
  "org.set_visitor_retention",
  "org.set_audit_retention",
  "account.create",
  "account.remove",
  "account.disable",
  "account.enable",
  "account.set_gates",
  "account.resend_invite",
  "unit.create",
  "unit.update",
  "unit.set_owner",
  "unit.delete",
  "unit.bulk_import",
  "gate.create",
  "gate_device.approve",
  "gate_device.bind_gate",
  "gate_device.set_pin",
  "gate_device.change_pin",
  "gate_device.revoke",
  "gate_device.set_display_mode",
  "api_key.create",
  "api_key.revoke",
  "pass.revoke",
  "pass.revoke_guest",
  // « Arrivé » on a self-fill visitor who never completed the form: the agent
  // checked ID and let them in (spec 2026-09-23 §8).
  "pass.confirm_incomplete",
  // « Ignorer » on a borne fiche at the desk (spec 2026-09-27 §5.3). No PII
  // in the event: the fiche is wiped in the same transaction.
  "borne.dismiss",
  // Recording a sortie mutates an operational row that already existed, so
  // both directions are audited: `exit.record` closes a visit (one row, or a
  // whole group in one press), `exit.reopen` takes a mis-tap back.
  "exit.record",
  "exit.reopen",
  "occupancy.start",
  "occupancy.end",
  "parcel.deposit",
  "parcel.hand_over",
  "amenity.create",
  "amenity.update",
  "amenity.archive",
  "booking.create",
  "booking.approve",
  "booking.reject",
  "booking.cancel",
  "feature.enable",
  "feature.disable",
  // Custom-form configuration: a publish (new version live) and a
  // « Désactiver » (the type no longer requires a form).
  "form.publish",
  "form.archive",
  "form.review",
  "document.view",
  "document.delete",
  "document.purge",
  "export.activity",
  "export.report",
  "auth.login",
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

export function auditActionFamily(action: string): string {
  return action.split(".")[0] ?? action;
}

/**
 * Distinct action families. Hand-maintained as a literal tuple (rather than
 * derived via `AUDIT_ACTIONS.map(auditActionFamily)`) because
 * `auditActionFamily`'s return type is plain `string` — deriving it would
 * lose the closed-union narrowing `z.enum` needs below. A test asserts this
 * stays in sync with `AUDIT_ACTIONS`.
 *
 * This is also the closed input set for `auditListInput.family`, which is
 * spliced into a SQL LIKE pattern in packages/api/router/audit.ts — a
 * regex-based allowlist (e.g. `^[a-z_]+$`) would still admit `_`, a LIKE
 * single-char wildcard, so only a closed enum actually closes that off.
 * Task 12's web code reuses this same list for the family filter's UI.
 */
export const AUDIT_FAMILIES = [
  "org",
  "account",
  "unit",
  "gate",
  "gate_device",
  "borne",
  "api_key",
  "pass",
  "exit",
  "occupancy",
  "parcel",
  "amenity",
  "booking",
  "feature",
  "form",
  "document",
  "export",
  "auth",
] as const;
export type AuditFamily = (typeof AUDIT_FAMILIES)[number];

/** Visibility tiers (read-time clamp only — storage is always AUDIT_HOT_MONTHS). */
export const AUDIT_RETENTION_TIERS = [12, 24] as const;
export const AUDIT_HOT_MONTHS = 24;

export function journalWindowStart(now: Date, months: number): Date {
  const d = new Date(now);
  d.setUTCMonth(d.getUTCMonth() - months);
  return d;
}

export const setAuditRetentionInput = z.object({
  organizationId: z.string().min(1),
  months: z.union([z.literal(12), z.literal(24)]),
});

export const auditListInput = z.object({
  limit: z.number().int().min(1).max(100),
  cursor: z.string().optional(),
  // Closed to AUDIT_FAMILIES: this value is spliced into a LIKE pattern
  // (`${family}.%`) in packages/api/router/audit.ts, so anything short of an
  // exact allowlist (a regex included — `_` is itself a LIKE single-char
  // wildcard) would still let a caller shape the filter with a metacharacter.
  family: z.enum([...AUDIT_FAMILIES]).optional(),
  from: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  to: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
});
export type AuditListInput = z.infer<typeof auditListInput>;
